import { useEffect, useState } from 'react';
import { listLinks, createLink, revokeLink, type LinkToken } from '../api/recruitingLinks';

const inputCls =
  'bg-dg-panel border border-dg-border rounded-md px-3 py-2 text-sm text-white placeholder-dg-mist/60 focus:outline-none focus:border-dg-red';

function StatusPill({ status }: { status: LinkToken['status'] }) {
  const cls: Record<string, string> = {
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    used: 'bg-dg-red/15 text-dg-red border-dg-red/30',
    expired: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    revoked: 'bg-dg-mist/15 text-dg-mist border-dg-border',
  };
  return (
    <span className={`text-[11px] uppercase tracking-wider border rounded px-2 py-0.5 ${cls[status]}`}>
      {status}
    </span>
  );
}

export default function RecruitingLinks() {
  const [tokens, setTokens] = useState<LinkToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'' | 'active' | 'used' | 'expired' | 'revoked'>('');
  const [showModal, setShowModal] = useState(false);
  const [createSource, setCreateSource] = useState('');
  const [createExpires, setCreateExpires] = useState<number>(30);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ url: string; plaintext: string } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { tokens } = await listLinks(filter ? { status: filter } : undefined);
      setTokens(tokens);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function onCreate() {
    setCreating(true);
    setError(null);
    try {
      const r = await createLink({ source: createSource, expiresInDays: createExpires });
      setCreated({ url: r.url, plaintext: r.plaintext });
      setCreateSource('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    if (!confirm('Revoke this link? It will no longer accept submissions.')) return;
    try {
      await revokeLink(id);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to revoke');
    }
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text).catch(() => {});
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="h-display text-3xl mb-1">Apply Links</h1>
          <p className="text-dg-mist text-sm">
            Generate one-time application links to send to candidates.
          </p>
        </div>
        <button
          onClick={() => { setShowModal(true); setCreated(null); }}
          className="px-4 py-2 rounded-md bg-dg-red hover:bg-dg-red-hover text-sm font-medium"
        >
          + Generate New Link
        </button>
      </div>

      {error ? (
        <div className="mb-4 px-4 py-3 rounded border border-dg-red/40 bg-dg-red/10 text-sm">{error}</div>
      ) : null}

      <div className="flex items-center gap-3 mb-4 text-sm">
        <span className="text-dg-mist">Filter:</span>
        <select className={inputCls} value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="used">Used</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      <div className="bg-dg-panel/60 border border-dg-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-dg-mist text-sm">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="p-6 text-dg-mist text-sm">No links yet. Generate one to get started.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-dg-panel border-b border-dg-border text-dg-mist">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Expires</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t._id} className="border-b border-dg-border last:border-0">
                  <td className="px-4 py-3">{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-dg-text-2">{t.source || '—'}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-dg-text-2">{new Date(t.expiresAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {t.status === 'active' ? (
                      <button
                        onClick={() => onRevoke(t._id)}
                        className="text-dg-red hover:text-dg-red-hover text-xs uppercase tracking-wider"
                      >
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal ? (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
          <div className="bg-dg-ink border border-dg-border rounded-lg max-w-md w-full p-6">
            {created ? (
              <>
                <h2 className="h-display text-xl mb-3" style={{ color: 'var(--dg-red)' }}>Link Created</h2>
                <p className="text-dg-mist text-sm mb-4">
                  Copy this URL and send it to your candidate. It can only be used once.
                </p>
                <div className="bg-dg-panel border border-dg-border rounded p-3 break-all text-sm mb-4">
                  {created.url}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => copy(created.url)}
                    className="px-4 py-2 rounded-md bg-dg-red hover:bg-dg-red-hover text-sm font-medium"
                  >
                    Copy URL
                  </button>
                  <button
                    onClick={() => { setShowModal(false); setCreated(null); }}
                    className="px-4 py-2 rounded-md border border-dg-border hover:border-dg-border-strong text-sm"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="h-display text-xl mb-4">Generate Apply Link</h2>
                <div className="space-y-4 mb-5">
                  <label className="block">
                    <div className="text-xs uppercase tracking-wider text-dg-mist mb-1.5">
                      Source / Campaign Tag <span className="text-dg-mist/70">(optional)</span>
                    </div>
                    <input
                      className={`${inputCls} w-full`}
                      placeholder="e.g. facebook-ad-may2026"
                      value={createSource}
                      onChange={(e) => setCreateSource(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs uppercase tracking-wider text-dg-mist mb-1.5">
                      Expires In (days)
                    </div>
                    <input
                      className={`${inputCls} w-full`}
                      type="number"
                      min={1}
                      max={180}
                      value={createExpires}
                      onChange={(e) => setCreateExpires(Number(e.target.value))}
                    />
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 rounded-md border border-dg-border hover:border-dg-border-strong text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onCreate}
                    disabled={creating}
                    className="px-4 py-2 rounded-md bg-dg-red hover:bg-dg-red-hover disabled:opacity-60 text-sm font-medium"
                  >
                    {creating ? 'Creating…' : 'Generate'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
